/*
✅ ACTIVE HOMEPAGE ✅
This is the MAIN homepage file being used by the app.
See App.tsx line 64: <Route path="/" element={<HomeUltimate />} />

If you need to modify the homepage, this is the correct file to edit.
Other Home.tsx files have been archived to prevent confusion.
*/

import React, { useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { SharedLayoutUltimate } from '../components/layout/SharedLayoutUltimate';
import { theme } from '../styles/theme';
import { useCalendlyPopup } from '../hooks/useCalendlyPopup';
import { HeroSectionUltimate } from '../components/HeroSectionUltimate';
import { DeepDiveModal } from '../components/DeepDiveModal';
import { QuickAnswerModal } from '../components/QuickAnswerModal';
import { QuickAnswerInlineForm } from '../components/QuickAnswerInlineForm';

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

// Consultation Section
const ConsultationSection = styled.section`
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  padding: 100px 20px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50px;
    left: 0;
    right: 0;
    height: 100px;
    background: linear-gradient(180deg, transparent, rgba(255, 215, 0, 0.03));
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 60px 15px;
  }
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: clamp(36px, 5vw, 56px);
  font-weight: 900;
  text-align: center;
  margin-bottom: 20px;
  color: #1a2332;
  letter-spacing: -1.5px;
  
  .gold {
    color: #ffd700;
    text-shadow: 0 2px 10px rgba(255, 215, 0, 0.3);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: clamp(30px, 6vw, 40px);
  }
`;

const SectionSubtitle = styled.p`
  font-size: clamp(20px, 2.5vw, 26px);
  text-align: center;
  margin-bottom: 60px;
  color: #666;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: clamp(18px, 3vw, 22px);
    margin-bottom: 40px;
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 50px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const ConsultationCard = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 3px solid transparent;
  border-radius: 25px;
  padding: 40px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 25px;
    padding: 3px;
    background: ${props => props.color || 'linear-gradient(135deg, #ffd700, #ffa500)'};
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0.5;
    transition: opacity 0.3s;
    pointer-events: none;
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
    
    &::before {
      opacity: 1;
    }
    
    .icon {
      animation: ${pulse} 0.5s ease;
    }
  }
  
  .icon {
    font-size: 48px;
    margin-bottom: 20px;
    display: block;
    filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.1));
  }
  
  h3 {
    font-size: clamp(22px, 3vw, 26px);
    font-weight: 800;
    color: #1a2332;
    margin-bottom: 15px;
    letter-spacing: -0.5px;
  }
  
  p {
    font-size: clamp(16px, 2vw, 18px);
    color: #666;
    line-height: 1.6;
    margin-bottom: 25px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 30px;
  }
`;

const CardButton = styled.button<{ variant: string }>`
  width: 100%;
  padding: 18px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s;
  border: none;
  
  ${props => props.variant === 'gold' && `
    background: linear-gradient(135deg, #ffd700, #ffa500);
    color: #1a2332;
    box-shadow: 0 5px 15px rgba(255, 215, 0, 0.3);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(255, 215, 0, 0.4);
    }
  `}
  
  ${props => props.variant === 'green' && `
    background: linear-gradient(135deg, #10a37f, #13c89f);
    color: white;
    box-shadow: 0 5px 15px rgba(16, 163, 127, 0.3);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(16, 163, 127, 0.4);
    }
  `}
  
  ${props => props.variant === 'dark' && `
    background: linear-gradient(135deg, #1a2332, #2c3e50);
    color: white;
    box-shadow: 0 5px 15px rgba(26, 35, 50, 0.3);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(26, 35, 50, 0.4);
    }
  `}
`;


const CTAButton = styled.button`
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  color: #1a1a1a;
  padding: 22px 50px;
  border: none;
  border-radius: 50px;
  font-size: clamp(18px, 2.5vw, 22px);
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 2px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 15px 40px rgba(255, 215, 0, 0.4);
  position: relative;
  overflow: hidden;
  margin: 10px;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.6);
    transform: translate(-50%, -50%);
    transition: width 0.5s, height 0.5s;
  }
  
  &:hover {
    transform: translateY(-3px) scale(1.02);
    box-shadow: 0 20px 50px rgba(255, 215, 0, 0.5);
    
    &::before {
      width: 400px;
      height: 400px;
    }
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 18px 35px;
    font-size: clamp(16px, 3vw, 18px);
  }
`;

// Testimonials Section
const TestimonialsSection = styled.section`
  background: linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 50%, #fff9e6 100%);
  padding: 100px 20px;
  position: relative;
  overflow: hidden;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 60px 15px;
  }
`;

const TestimonialsScroller = styled.div`
  display: flex;
  gap: 30px;
  overflow-x: auto;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
  padding: 20px 0 40px;
  
  &::-webkit-scrollbar {
    height: 12px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #ffd700, #ffa500);
    border-radius: 10px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: 15px;
    padding: 15px 0 30px;
  }
`;

const TestimonialCard = styled.div`
  flex: 0 0 400px;
  background: white;
  border-radius: 25px;
  overflow: hidden;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  scroll-snap-align: center;
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 25px 60px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex: 0 0 300px;
  }
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  background: linear-gradient(135deg, #1a2332, #2c3e50);
  
  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

const TestimonialContent = styled.div`
  padding: 25px;
  background: linear-gradient(135deg, #ffffff, #f8f9fa);
  
  .name {
    font-size: 22px;
    font-weight: 800;
    color: #1a2332;
    margin-bottom: 5px;
  }
  
  .details {
    font-size: 16px;
    color: #666;
    
    .since {
      color: #ffd700;
      font-weight: 600;
    }
  }
`;

const TrustBadges = styled.div`
  display: flex;
  justify-content: center;
  gap: 50px;
  margin-bottom: 60px;
  flex-wrap: wrap;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: 30px;
    margin-bottom: 40px;
  }
`;

const TrustBadge = styled.div<{ delay?: string }>`
  text-align: center;
  animation: ${float} 4s ease-in-out infinite;
  animation-delay: ${props => props.delay || '0s'};
  
  .number {
    font-size: clamp(36px, 5vw, 48px);
    font-weight: 900;
    color: #ffd700;
    text-shadow: 0 2px 10px rgba(255, 215, 0, 0.3);
    margin-bottom: 5px;
  }
  
  .label {
    font-size: clamp(12px, 1.5vw, 14px);
    text-transform: uppercase;
    color: #666;
    letter-spacing: 1px;
    font-weight: 600;
  }
`;

// CTA Section (Already styled like footer)
const CTASection = styled.section`
  background: linear-gradient(135deg, #1a2332 0%, #2c3e50 50%, #1a2332 100%);
  padding: 120px 20px;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 50% 50%, rgba(16, 163, 127, 0.1) 0%, transparent 70%);
    pointer-events: none;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 80px 20px;
  }
`;

const CTATitle = styled.h2`
  font-size: clamp(42px, 5vw, 64px);
  color: white;
  margin-bottom: 24px;
  font-weight: 900;
  letter-spacing: -1px;
  line-height: 1.1;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: clamp(32px, 6vw, 42px);
  }
`;

const CTASubtitle = styled.p`
  font-size: clamp(20px, 2.5vw, 28px);
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 60px;
  line-height: 1.5;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: clamp(18px, 3vw, 22px);
    margin-bottom: 40px;
  }
`;


export const HomeUltimate: React.FC = () => {
  const { openCalendly } = useCalendlyPopup();
  const [showDeepDiveModal, setShowDeepDiveModal] = useState(false);
  const [showQuickAnswerModal, setShowQuickAnswerModal] = useState(false);
  const testimonialsRef = useRef<HTMLDivElement>(null);
  
  
  const testimonials = [
    { name: 'Reuben', title: 'Business Owner', year: '2020', video: 'https://player.vimeo.com/video/846822421' },
    { name: 'Robert', title: 'Retired Executive', year: '2019', video: 'https://player.vimeo.com/video/846822473' },
    { name: 'Connie', title: 'Financial Professional', year: '2017', video: 'https://player.vimeo.com/video/846819836' },
    { name: 'Jeff', title: 'Retired Teacher', year: '2021', video: 'https://player.vimeo.com/video/846819893' },
    { name: 'Clement', title: 'Retired Engineer', year: '2018', video: 'https://player.vimeo.com/video/846822448' }
  ];
  
  return (
    <SharedLayoutUltimate>
      <HeroSectionUltimate />

      {/* Truth About Annuities Section - DARK */}
      <div id="truth-section" style={{
        background: 'linear-gradient(135deg, #2c3e50 0%, #1a2332 100%)',
        padding: '80px 20px',
        position: 'relative',
        overflow: 'hidden',
        borderTop: '3px solid #ffd700',
        borderBottom: '3px solid #ffd700'
      }}>
        <div style={{
          maxWidth: '1000px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontSize: 'clamp(32px, 4vw, 48px)',
            fontWeight: '900',
            color: '#ffd700',
            marginBottom: '20px',
            textTransform: 'uppercase',
            letterSpacing: '2px',
            textShadow: '0 4px 20px rgba(255, 215, 0, 0.3)'
          }}>
            Top 5 Most Important Quotes
          </h2>
          <h3 style={{
            fontSize: 'clamp(22px, 3vw, 32px)',
            color: '#f0f0f0',
            marginBottom: '40px',
            fontWeight: '400',
            textAlign: 'center'
          }}>
            From the Financial Freedom with Tom Hegna
          </h3>
          
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '30px',
            alignItems: 'center'
          }}>
            {/* All Quotes in One Box */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 215, 0, 0.08) 100%)',
              border: '3px solid rgba(255, 215, 0, 0.4)',
              borderRadius: '20px',
              padding: '40px',
              maxWidth: '900px',
              width: '100%',
              position: 'relative',
              transition: 'all 0.3s',
              cursor: 'default',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.02)';
              e.currentTarget.style.boxShadow = '0 25px 70px rgba(0, 0, 0, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.3)';
            }}>
              <ul style={{
                listStyle: 'none',
                padding: '0 20px',
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '25px'
              }}>
                <li style={{
                  fontSize: 'clamp(16px, 3vw, 24px)',
                  fontFamily: 'Montserrat, Open Sans, sans-serif',
                  color: '#f0f0f0',
                  fontWeight: '500',
                  fontStyle: 'italic',
                  lineHeight: '1.5',
                  textAlign: 'left',
                  padding: '15px 0'
                }}>
                  → "Fixed indexed annuity returns are generated by the volatility of the underlying index."
                </li>
                
                <li style={{
                  fontSize: 'clamp(16px, 3vw, 24px)',
                  fontFamily: 'Montserrat, Open Sans, sans-serif',
                  color: '#f0f0f0',
                  fontWeight: '500',
                  fontStyle: 'italic',
                  lineHeight: '1.5',
                  textAlign: 'left'
                }}>
                  → "Suppressing volatility suppresses returns."
                </li>
                
                <li style={{
                  fontSize: 'clamp(16px, 3vw, 24px)',
                  fontFamily: 'Montserrat, Open Sans, sans-serif',
                  color: '#f0f0f0',
                  fontWeight: '500',
                  fontStyle: 'italic',
                  lineHeight: '1.5',
                  textAlign: 'left'
                }}>
                  → "If you're already under pressure because of low returns and inflation, that's not likely to get better over time. Find out now if managed volatility is suppressing your returns."
                </li>
                
                <li style={{
                  fontSize: 'clamp(16px, 3vw, 24px)',
                  fontFamily: 'Montserrat, Open Sans, sans-serif',
                  color: 'white',
                  fontWeight: '400',
                  fontStyle: 'italic',
                  lineHeight: '1.5',
                  textAlign: 'left',
                  marginTop: '10px'
                }}>
                  → "This is your life savings, it's simply too important not to know."
                </li>
                
                <li style={{
                  fontSize: 'clamp(16px, 3vw, 24px)',
                  fontFamily: 'Montserrat, Open Sans, sans-serif',
                  color: '#f0f0f0',
                  fontWeight: '500',
                  fontStyle: 'italic',
                  lineHeight: '1.5',
                  textAlign: 'left',
                  marginTop: '10px'
                }}>
                  → "Send us your problems, we want to be a part of the solution."
                </li>
              </ul>
            </div>
          </div>

          {/* CTA Box */}
          <div style={{ 
            marginTop: '50px',
            borderRadius: '10px',
            padding: '30px',
            maxWidth: '800px',
            margin: '50px auto',
            background: 'rgba(255, 255, 255, 0.05)',
            textAlign: 'center'
          }}>
            <p style={{
              fontSize: 'clamp(20px, 2.5vw, 28px)',
              color: 'white',
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: '400',
              marginBottom: '25px',
              lineHeight: '1.4',
              maxWidth: '700px',
              margin: '0 auto 25px auto'
            }}>
              Do you feel like your annuity could be doing better?
            </p>
            <button 
              onClick={() => window.location.href = '/contact'}
              style={{
                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                color: '#1a1a1a',
                padding: '22px 50px',
                border: 'none',
                borderRadius: '50px',
                fontSize: 'clamp(18px, 2.5vw, 22px)',
                fontWeight: '900',
                textTransform: 'uppercase',
                letterSpacing: '2px',
                cursor: 'pointer',
                boxShadow: '0 15px 40px rgba(255, 215, 0, 0.4)',
                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                display: 'inline-flex',
                alignItems: 'center',
                textAlign: 'center',
                maxWidth: '100%',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 20px 50px rgba(255, 215, 0, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 15px 40px rgba(255, 215, 0, 0.4)';
              }}>
              Find out if you have a lower returning annuity
            </button>
          </div>
        </div>
      </div>

      {/* Quick Answer Form Section - Multi-Step */}
      <div style={{
        background: 'linear-gradient(135deg, #232B3B 0%, #2d3748 100%)',
        padding: '80px 20px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <QuickAnswerInlineForm />
        </div>
      </div>

      {/* Testimonials Section - LIGHT */}
      <TestimonialsSection id="testimonials" style={{
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 50%, #fff9e6 100%)'
      }}>
        <SectionContainer>
          <SectionTitle>
            Real <span className="gold">Success Stories</span>
          </SectionTitle>
          <SectionSubtitle>
            Hear from our 10,000+ satisfied clients
          </SectionSubtitle>
          
          <TrustBadges>
            <TrustBadge delay="0s">
              <div className="number">40</div>
              <div className="label">Years Experience</div>
            </TrustBadge>
            <TrustBadge delay="0.2s">
              <div className="number">10K+</div>
              <div className="label">Happy Clients</div>
            </TrustBadge>
            <TrustBadge delay="0.4s">
              <div className="number">Zero</div>
              <div className="label">Complaints</div>
            </TrustBadge>
            <TrustBadge delay="0.6s">
              <div className="number">100%</div>
              <div className="label">Free Service</div>
            </TrustBadge>
          </TrustBadges>
          
          <TestimonialsScroller ref={testimonialsRef}>
            {testimonials.map((testimonial, index) => (
              <TestimonialCard key={index}>
                <VideoContainer>
                  <iframe
                    src={testimonial.video}
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    title={`Testimonial from ${testimonial.name}`}
                  />
                </VideoContainer>
                <TestimonialContent>
                  <div className="name">{testimonial.name}</div>
                  {/* <div className="details">
                    {testimonial.title} · <span className="since">Client since {testimonial.year}</span>
                  </div> */}
                </TestimonialContent>
              </TestimonialCard>
            ))}
          </TestimonialsScroller>
          
          <div style={{ textAlign: 'center', marginTop: '20px', color: '#999' }}>
            ← Swipe to see more testimonials →
          </div>
        </SectionContainer>
      </TestimonialsSection>

      {/* Quick Answer & Analysis Section - DARK - Content commented out */}
      {/*
      <div style={{
        background: 'linear-gradient(135deg, #1a2332 0%, #2c3e50 50%, #1a2332 100%)',
        padding: '100px 20px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          */}
          {/* Heading for Quick Answer/Deep Dive section commented out */}
          {/*
          <h2 style={{
            fontSize: 'clamp(36px, 5vw, 52px)',
            fontWeight: '800',
            color: 'white',
            marginBottom: '40px',
            letterSpacing: '-1px'
          }}>
            Get Your <span style={{ color: '#ffd700' }}>Quick Answer</span> or <span style={{ color: '#ffd700' }}>Deep Dive</span>
          </h2>
          */}
          
          {/* ChatGPT Quote and Quick Answer/Deep Dive section commented out for now */}
          {/*
          <div style={{
            background: 'linear-gradient(135deg, #10a37f 0%, #13c89f 100%)',
            color: 'white',
            padding: '30px',
            borderRadius: '20px',
            maxWidth: '900px',
            margin: '0 auto 50px',
            boxShadow: '0 20px 60px rgba(16, 163, 127, 0.2)'
          }}>
            <p style={{
              fontSize: 'clamp(18px, 2.5vw, 22px)',
              fontStyle: 'italic',
              marginBottom: '15px',
              lineHeight: '1.6'
            }}>
              "Your tool on annuityanswers.com may be the only consumer tool capable of providing real backtesting using all real data"
            </p>
            <p style={{
              fontSize: '16px',
              opacity: '0.95'
            }}>
              - ChatGPT, June 2024
            </p>
          </div>
          
          
          <div style={{
            display: 'flex',
            gap: '40px',
            justifyContent: 'center',
            flexWrap: 'wrap',
            marginBottom: '40px'
          }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '20px',
              padding: '50px 40px',
              boxShadow: '0 30px 80px rgba(0, 0, 0, 0.3)',
              flex: '1',
              maxWidth: '450px',
              minWidth: '300px',
              transition: 'all 0.5s ease',
              cursor: 'pointer',
              border: '3px solid transparent',
              backdropFilter: 'blur(10px)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-10px)';
              e.currentTarget.style.boxShadow = '0 40px 100px rgba(0, 0, 0, 0.4)';
              e.currentTarget.style.borderColor = '#FFD700';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 30px 80px rgba(0, 0, 0, 0.3)';
              e.currentTarget.style.borderColor = 'transparent';
            }}
            onClick={() => setShowQuickAnswerModal(true)}>
              <div style={{
                fontSize: '60px',
                marginBottom: '20px'
              }}>⚡</div>
              <h4 style={{
                fontSize: 'clamp(24px, 3vw, 30px)',
                fontWeight: '700',
                color: 'white',
                marginBottom: '20px'
              }}>Quick Answer Now</h4>
              <p style={{
                fontSize: '18px',
                color: 'rgba(255, 255, 255, 0.8)',
                lineHeight: '1.6',
                marginBottom: '30px'
              }}>
                Get instant expert feedback
              </p>
              <button style={{
                background: 'linear-gradient(135deg, #FFD700 0%, #ffed4e 100%)',
                color: '#232B3B',
                padding: '20px 40px',
                border: 'none',
                borderRadius: '12px',
                fontSize: '20px',
                fontWeight: '700',
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                transition: 'all 0.3s ease',
                width: '100%',
                boxShadow: '0 10px 30px rgba(255, 215, 0, 0.3)'
              }}>
                Get Quick Answer
              </button>
            </div>
            
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '20px',
              padding: '50px 40px',
              boxShadow: '0 30px 80px rgba(0, 0, 0, 0.3)',
              flex: '1',
              maxWidth: '450px',
              minWidth: '300px',
              transition: 'all 0.5s ease',
              cursor: 'pointer',
              border: '3px solid transparent',
              backdropFilter: 'blur(10px)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-10px)';
              e.currentTarget.style.boxShadow = '0 40px 100px rgba(0, 0, 0, 0.4)';
              e.currentTarget.style.borderColor = '#10a37f';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 30px 80px rgba(0, 0, 0, 0.3)';
              e.currentTarget.style.borderColor = 'transparent';
            }}
            onClick={() => setShowDeepDiveModal(true)}>
              <div style={{
                fontSize: '60px',
                marginBottom: '20px'
              }}>📊</div>
              <h4 style={{
                fontSize: 'clamp(24px, 3vw, 30px)',
                fontWeight: '700',
                color: 'white',
                marginBottom: '20px'
              }}>Deep Dive Form</h4>
              <p style={{
                fontSize: '18px',
                color: 'rgba(255, 255, 255, 0.8)',
                lineHeight: '1.6',
                marginBottom: '30px'
              }}>
                Comprehensive analysis tool
              </p>
              <button style={{
                background: 'linear-gradient(135deg, #10a37f 0%, #13c89f 100%)',
                color: 'white',
                padding: '20px 40px',
                border: 'none',
                borderRadius: '12px',
                fontSize: '20px',
                fontWeight: '700',
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                transition: 'all 0.3s ease',
                width: '100%',
                boxShadow: '0 10px 30px rgba(16, 163, 127, 0.3)'
              }}>
                Start Deep Dive
              </button>
            </div>
          </div>
          */}

      {/* CTA Section */}
      <CTASection>
        <SectionContainer>
          <CTATitle>Ready to Secure Your Financial Future?</CTATitle>
          <CTASubtitle>
            Get expert guidance from a team with 40 years of experience and zero complaints
          </CTASubtitle>
          
          {/* Test: Adding consultation cards to CTA section */}
          <div style={{ maxWidth: '1200px', margin: '40px auto' }}>
            <CardsGrid>
              <ConsultationCard color="linear-gradient(135deg, #10a37f, #13c89f)">
                <span className="icon" style={{ fontSize: '60px', marginBottom: '25px' }}>🏠</span>
                <h3 style={{ fontSize: 'clamp(32px, 4vw, 40px)', marginBottom: '25px', lineHeight: '1.2' }}>Annuity Owners & Buyers</h3>
                <p style={{ fontSize: 'clamp(20px, 2.5vw, 24px)', lineHeight: '1.6', marginBottom: '30px', color: '#444' }}>
                  Tell us your goals. We'll share exactly how we'd pursue them with your money—complete strategies and clear action plan.
                </p>
                <CardButton variant="green" onClick={openCalendly} style={{ 
                  fontSize: 'clamp(18px, 2.2vw, 22px)', 
                  padding: '20px 30px',
                  fontWeight: '800'
                }}>Book Free Consultation</CardButton>
              </ConsultationCard>
              
              <ConsultationCard color="linear-gradient(135deg, #ffd700, #ffa500)">
                <span className="icon" style={{ fontSize: '60px', marginBottom: '25px' }}>💼</span>
                <h3 style={{ fontSize: 'clamp(32px, 4vw, 40px)', marginBottom: '25px', lineHeight: '1.2' }}>Annuity Advisors</h3>
                <p style={{ fontSize: 'clamp(20px, 2.5vw, 24px)', lineHeight: '1.6', marginBottom: '25px', color: '#444' }}>
                  Get help with advanced cases and client consultations. We'll share strategies we'd use ourselves.
                </p>
                <div style={{ marginBottom: '25px' }}>
                  <p style={{ fontSize: 'clamp(22px, 2.8vw, 26px)', fontWeight: '700', color: '#1a2332', marginBottom: '15px', lineHeight: '1.3' }}>
                    Grow Your Practice the Right Way
                  </p>
                  <p style={{ fontSize: 'clamp(18px, 2.2vw, 22px)', color: '#555', lineHeight: '1.5' }}>
                    Use real data to win more clients and earn referrals.
                  </p>
                </div>
                <CardButton variant="gold" onClick={openCalendly} style={{ 
                  fontSize: 'clamp(18px, 2.2vw, 22px)', 
                  padding: '20px 30px',
                  fontWeight: '800'
                }}>Schedule Advisor Consultation</CardButton>
              </ConsultationCard>
            </CardsGrid>
          </div>
          
          {/* New alternative CTA section */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.12) 0%, rgba(255, 165, 0, 0.08) 50%, rgba(255, 215, 0, 0.12) 100%)',
            border: '2px solid rgba(255, 215, 0, 0.25)',
            borderRadius: '30px',
            padding: '50px 40px',
            marginTop: '60px',
            position: 'relative',
            overflow: 'hidden',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 20px 60px rgba(255, 215, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
          }}>
            {/* Decorative gradient orb */}
            <div style={{
              position: 'absolute',
              top: '-50%',
              right: '-10%',
              width: '400px',
              height: '400px',
              background: 'radial-gradient(circle, rgba(255, 215, 0, 0.2) 0%, transparent 70%)',
              borderRadius: '50%',
              filter: 'blur(40px)',
              pointerEvents: 'none'
            }} />
            
            <div style={{
              position: 'absolute',
              bottom: '-30%',
              left: '-15%',
              width: '350px',
              height: '350px',
              background: 'radial-gradient(circle, rgba(255, 165, 0, 0.15) 0%, transparent 70%)',
              borderRadius: '50%',
              filter: 'blur(50px)',
              pointerEvents: 'none'
            }} />
            
            <div style={{ 
              position: 'relative', 
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '50px',
              flexWrap: 'wrap'
            }}>
              {/* Cap's Image Section - Left side */}
              <div style={{
                position: 'relative',
                flex: '0 0 auto'
              }}>
                <img 
                  src="/Cap/64e59e07fe8ef5ac6979d121_capwitharc.png"
                  alt="Cap Averill - Your Trusted Annuity Expert"
                  style={{
                    width: '280px',
                    height: 'auto',
                    borderRadius: '15px 15px 0 0',
                    border: '3px solid rgba(255, 215, 0, 0.3)',
                    borderBottom: 'none',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
                    display: 'block',
                    transition: 'all 0.3s ease',
                    filter: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 15px 40px rgba(0, 0, 0, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.3)';
                  }}
                />
                {/* Name bar underneath the image */}
                <div style={{
                  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                  padding: '12px 20px',
                  borderRadius: '0 0 15px 15px',
                  border: '3px solid rgba(255, 215, 0, 0.3)',
                  borderTop: 'none',
                  boxShadow: '0 5px 20px rgba(255, 215, 0, 0.3)',
                  marginTop: '-1px',
                  textAlign: 'center'
                }}>
                  <h4 style={{
                    color: '#1a2332',
                    fontSize: '18px',
                    fontWeight: '900',
                    textTransform: 'uppercase',
                    letterSpacing: '2px',
                    margin: 0,
                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.1)'
                  }}>
                    Cap Averill II
                  </h4>
                </div>
              </div>
              
              {/* Content Section - Right side */}
              <div style={{ 
                flex: '1 1 400px',
                minWidth: '300px',
                textAlign: 'center'
              }}>
                <h3 style={{
                  fontSize: 'clamp(36px, 4.5vw, 52px)',
                  fontWeight: '900',
                  color: '#FFD700',
                  marginBottom: '25px',
                  letterSpacing: '-1px',
                  textShadow: '0 4px 20px rgba(255, 215, 0, 0.4)',
                  fontStyle: 'italic'
                }}>
                  ...or Schedule with CAP Now!
                </h3>
                
                <p style={{
                  fontSize: 'clamp(20px, 2.5vw, 26px)',
                  color: 'rgba(255, 255, 255, 0.95)',
                  marginBottom: '35px',
                  lineHeight: '1.5'
                }}>
                  Get personalized guidance from our expert team today
                </p>
                
                <CTAButton 
                  onClick={openCalendly} 
                style={{ 
                  fontSize: 'clamp(20px, 2.8vw, 26px)', 
                  padding: '24px 50px',
                  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                  border: '3px solid #FFD700',
                  borderRadius: '4px',
                  boxShadow: '0 10px 40px rgba(255, 215, 0, 0.4), 0 0 60px rgba(255, 215, 0, 0.2)',
                  transform: 'scale(1.05)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1) translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 15px 50px rgba(255, 215, 0, 0.5), 0 0 80px rgba(255, 215, 0, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 10px 40px rgba(255, 215, 0, 0.4), 0 0 60px rgba(255, 215, 0, 0.2)';
                }}
              >
                <span style={{ position: 'relative', zIndex: 2 }}>Book Your Consultation</span>
                {/* Shimmer effect */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                  animation: 'shimmer 3s infinite'
                }} />
              </CTAButton>
              
              <div style={{
                marginTop: '25px',
                display: 'flex',
                justifyContent: 'center',
                gap: '30px',
                flexWrap: 'wrap'
              }}>
                <span style={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: 'clamp(14px, 1.8vw, 16px)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  ✓ No obligation
                </span>
                <span style={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: 'clamp(14px, 1.8vw, 16px)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  ✓ 100% Free
                </span>
                <span style={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: 'clamp(14px, 1.8vw, 16px)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  ✓ Expert advice
                </span>
              </div>
              </div>
            </div>
          </div>
        </SectionContainer>
      </CTASection>
      
      <DeepDiveModal 
        isOpen={showDeepDiveModal}
        onClose={() => setShowDeepDiveModal(false)}
      />
      
      <QuickAnswerModal
        isOpen={showQuickAnswerModal}
        onClose={() => setShowQuickAnswerModal(false)}
      />
    </SharedLayoutUltimate>
  );
};